import { useMutation, useQuery } from "@tanstack/react-query";
import { stopCharging } from "../api/chargingStations";
import {
  getTransactionDetails,
  getTransactionDetailsInfo,
  getTransactionId,
} from "../api/transactions";
import { useCallback, useEffect, useState } from "react";
import { useStationContext } from "../context/stationContext";
import { api } from "../lib/axios";
import TimeFormatter from "../components/TimeFormatter";
import { BatteryFull, Building, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { CONSTANTS } from "../constants/constants";
import { toast } from "react-toastify";
import { io } from "socket.io-client";
import InvoicePDF from "./Invoice";

export type TransactionDetailsType = {
  chargeBoxId: string;
  price: number | null;
  consumptionKWh?: string;
  totalDuration?: number;
  startedSession?: string;
  endedSession?: string;
  stateOfCharge?: number;
  id?: number;
  pricePerKWh?: number;
};

interface SocketData {
  stationId: string;
  sessionId: string;
  transactionId: string;
}

const Transaction = () => {
  const { stationId } = useStationContext();
  const navigate = useNavigate();
  const [isChargingDone, setIsChargingDone] = useState(false);
  const cachedStationId = stationId || localStorage.getItem("stationId");
  const cachedSessionId = localStorage.getItem("sessionId");
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [email, setEmail] = useState("");
  const [transactionDetails, setTransactionDetails] =
    useState<TransactionDetailsType>({
      chargeBoxId: "",
      price: null,
      consumptionKWh: "",
      totalDuration: 0,
    });

  const { data } = useQuery({
    queryKey: ["station-id", cachedStationId],
    queryFn: async () => {
      const { data } = await api.get(`/charge/${cachedStationId}/`);
      return data;
    },
    enabled: !!cachedStationId && !isChargingDone,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const fetchTransactionData = useCallback(
    async (stationId: string, sessionId: string, transactionId: string) => {
      try {
        if (
          stationId.toString() === cachedStationId?.toString() &&
          sessionId.toString() === cachedSessionId?.toString() &&
          transactionId.length > 0
        ) {
          const response = await getTransactionDetailsInfo(transactionId);
          const consumptionWh = response.stop.totalConsumptionWh;
          const totalDuration = response.stop.totalDurationSecs;

          const price =
            Math.round(
              (consumptionWh / 1000) *
                parseFloat(data?.pricePerKWh || "0") *
                100
            ) / 100;

          const consumptionKWh = (consumptionWh / 1000).toFixed(2).toString();

          setTransactionDetails({
            chargeBoxId: response.chargeBoxID,
            id: response.sessionId,
            price,
            consumptionKWh,
            totalDuration,
            startedSession: response.timestamp,
            endedSession: response.stop.timestamp,
            stateOfCharge: response.stop.stateOfCharge,
          });
          setIsChargingDone(true);
        }
      } catch (error) {
        console.error("Error fetching transaction data:", error);
      }
    },
    [cachedStationId, cachedSessionId, data?.pricePerKWh]
  );

  useEffect(() => {
    const socket = io(import.meta.env.VITE_BACKEND_URL, {
      transports: ["websocket"],
    });

    const handleTransactionUpdate = (data: SocketData) => {
      fetchTransactionData(data.stationId, data.sessionId, data.transactionId);
    };

    socket.on("transaction-update", handleTransactionUpdate);

    return () => {
      socket.off("transaction-update", handleTransactionUpdate);
      socket.disconnect();
    };
  }, [fetchTransactionData]);

  const stopChargingMutation = useMutation({
    mutationFn: () => stopCharging(cachedStationId as string),
    onSuccess: async () => {
      try {
        setTimeout(async () => {
          const response = await getTransactionDetails(
            cachedStationId as string
          );
          const isStopped = Boolean(response.stop);
          if (isStopped) {
            const consumptionWh = response.stop.totalConsumptionWh;
            const totalDuration = response.stop.totalDurationSecs;

            const price =
              Math.round(
                (consumptionWh / 1000) * parseFloat(data.pricePerKWh) * 100
              ) / 100;
            const consumptionKWh = (consumptionWh / 1000).toFixed(2).toString();
            const amount =
              (consumptionWh / 1000) * (parseFloat(data.pricePerKWh) * 100);
            const amountToCapture = Math.round(amount * 100) / 100;

            setTransactionDetails((prevState) => ({
              ...prevState,
              price: price,
              consumptionKWh: consumptionKWh,
              totalDuration: totalDuration,
              id: response.sessionId,
              startedSession: response.timestamp,
              endedSession: response.stop.timestamp,
              stateOfCharge: response.stop.stateOfCharge,
              pricePerKWh: parseFloat(data.pricePerKWh),
            }));

            if (consumptionWh / 1000 > 0.5 || totalDuration > 120) {
              await handlePay(amountToCapture);
            } else {
              await cancelPay();
            }
          }
        }, 500);
      } catch (error) {
        toast.error(t("common.errorStop"));
        console.error("Error during stop charging process:", error);
      }
    },
    onError: (error) => {
      console.error("Mutation error:", error);
    },
  });

  useEffect(() => {
    if (isChargingDone) return;
    let timeout;
    timeout = setTimeout(async () => {
      const fetchTransactionId = async () => {
        const response = await getTransactionId(cachedStationId as string);
        if (response.chargeBoxID) {
          setTransactionDetails((prevState) => ({
            ...prevState,
            chargeBoxId: response.chargeBoxID,
          }));
        }
      };

      await fetchTransactionId();
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [cachedStationId]);

  useEffect(() => {
    if (isChargingDone) {
      Object.values(CONSTANTS.LOCAL_STORAGE_KEYS).forEach((key) =>
        localStorage.removeItem(key)
      );
    }
  }, [isChargingDone]);

  useEffect(() => {
    if (isChargingDone) {
      const redirectTimer = setTimeout(() => {
        navigate(`/${lang}`);
      }, 30000);

      return () => clearTimeout(redirectTimer);
    }
  }, [isChargingDone, lang, navigate]);

  useEffect(() => {
    const hasRequiredData = Object.values(CONSTANTS.LOCAL_STORAGE_KEYS).every(
      (key) => localStorage.getItem(key)
    );

    if (!hasRequiredData) {
      navigate(`/${lang}`);
    }
  }, [navigate]);

  const handlePay = async (amountToCapture: number) => {
    const response = await api.post("capture-held-funds", {
      sessionId: cachedSessionId,
      amountToCapture: amountToCapture,
    });
    setEmail(response.data.email);
    setIsChargingDone(true);
  };
  const cancelPay = async () => {
    await api.post("cancel-payment", {
      sessionId: cachedSessionId,
    });
    setIsChargingDone(true);
  };

  const handleStopCharging = () => {
    setShowConfirmModal(false);
    stopChargingMutation.mutate();
  };

  return (
    <>
      <div className="min-h-screen w-full flex gap-4 flex-col items-center justify-center">
        <div className="h-[250px] w-full flex flex-col rounded-xl p-2">
          {isChargingDone && (
            <>
              <div className="flex justify-between items-center gap-2 py-4 border-t font-semibold">
                <div className="flex justify-start items-center gap-2">
                  <Building size={"24px"} />
                  <p>{t("transaction.chargerId")}</p>
                </div>
                <p>{transactionDetails?.chargeBoxId}</p>
              </div>

              <div className="flex justify-between items-center gap-2 py-4 border-t font-semibold">
                <div className="flex justify-start items-center gap-2">
                  <Info size={"24px"} />
                  <p>{t("transaction.price")}</p>
                </div>
                <p>
                  {transactionDetails?.price} {data?.currency}
                </p>
              </div>
              <div className="flex justify-between items-center gap-2 py-4 border-t font-semibold">
                <div className="flex justify-start items-center gap-2">
                  <BatteryFull size={"24px"} />
                  <p>{t("transaction.consumption")}</p>
                </div>
                <p>{transactionDetails.consumptionKWh} kWh</p>
              </div>
              <TimeFormatter
                seconds={(transactionDetails?.totalDuration as number) || 0}
                startedSession={transactionDetails?.startedSession as string}
                endedSession={transactionDetails?.endedSession as string}
              />
            </>
          )}
          {showConfirmModal && (
            <div className="fixed inset-0 z-[100] bg-black bg-opacity-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
                <h3 className="text-lg font-semibold mb-4">
                  {t("transaction.confirmStop")}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t("transaction.confirmStopMessage")}
                </p>
                <div className="flex gap-3">
                  <button
                    className="flex-1 px-4 py-2 bg-gray-200 rounded-xl font-semibold"
                    onClick={() => setShowConfirmModal(false)}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl font-semibold"
                    onClick={handleStopCharging}
                  >
                    {t("common.confirm")}
                  </button>
                </div>
              </div>
            </div>
          )}
          {!isChargingDone && (
            <>
              <div className="flex justify-between items-center gap-2 py-4 border-t font-semibold">
                <div className="flex justify-start items-center gap-2">
                  <Building size={"24px"} />
                  <p>{t("transaction.chargerId")}</p>
                </div>
                <p>{transactionDetails?.chargeBoxId}</p>
              </div>
              <div className="flex justify-between items-center gap-2 py-4 border-t border-b font-semibold">
                <div className="flex justify-start items-center gap-2">
                  <Info size={"24px"} />
                  <p>{t("transaction.pricePerKWh")}</p>
                </div>
                <p>
                  {data?.pricePerKWh} {data?.currency}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      {isChargingDone && (
        <InvoicePDF
          transactionDetails={transactionDetails}
          pricePerKWh={data.pricePerKWh}
          isChargingDone
          email={email}
        />
      )}
      {!isChargingDone && (
        <div className="flex w-full items-center left-0 justify-center absolute bottom-0 h-[100px] px-4">
          <button
            className="text-center px-3 py-2 w-full  bg-red-600 hover:bg-red-700 text-white font-semibold rounded-2xl"
            onClick={() => setShowConfirmModal(true)}
          >
            {t("transaction.stop")}
          </button>
        </div>
      )}
    </>
  );
};

export default Transaction;
